<template>

  <v-layout column align-center class="settings-page">
    <v-card id="device" class="device-card mx-auto mb-5" width="100%" max-width="400" flat>
      <v-card-title class="device-card__header py-4">
        <div v-if="$vuetify.breakpoint.mdAndDown" class="device-card__back-rail">
          <div class="device-card__back-inner">
            <v-btn
              id="device-back"
              class="device-card__back"
              icon
              :aria-label="$t('app.form.back.title')"
              @click="previous()"
            >
              <v-icon>{{ chevronBack }}</v-icon>
            </v-btn>
          </div>
        </div>
        <mrx-translation :token="`app.device.title`" class="device-card__title"></mrx-translation>
      </v-card-title>

      <!-- Tab Navigation -->
      <div class="device-tabs-wrapper">
        <div class="device-tabs">
          <button
            id="device-tab-status"
            class="device-tab"
            :class="{ 'device-tab--active': activeTab === 'status' }"
            @click="activeTab = 'status'"
          >
            <mrx-translation token="app.device.tabs.status"></mrx-translation>
          </button>
          <button
            id="device-tab-logs"
            class="device-tab"
            :class="{ 'device-tab--active': activeTab === 'logs' }"
            @click="activeTab = 'logs'"
          >
            <mrx-translation token="app.device.tabs.logs"></mrx-translation>
          </button>
        </div>
      </div>

      <v-card-text class="px-0 device-card-content">

        <!-- STATUS Tab Content -->
        <div v-show="activeTab === 'status'" class="status-tab-wrapper">
          <v-list class="device-list pa-0">
            <v-list-item class="device-row">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="badgeColor(connection)" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{ icons.mdiWifi }}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation token="app.device.list.connection_status.title"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle class="d-flex align-center">
                  <mrx-translation :prepend="$n(mbps)" token="app.device.list.connection_speed.title"></mrx-translation>
                  <span class="device-row__dot">·</span>
                  <mrx-translation :prepend="estimatedTime" token="app.device.list.connection_time.title"></mrx-translation>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="device-row__value">
                <mrx-translation :token="connectionStatusText"></mrx-translation>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="device-row">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="badgeColor(records === 0)" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{ icons.mdiCloudUpload }}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.records_in_queue.title`"></mrx-translation></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="device-row__value">{{ records }}</v-list-item-action>
            </v-list-item>

            <v-list-item v-if="(!syncActive && records > 0 && connection) || getMode('showUploadButton')" class="device-row">
              <v-list-item-content>
                <v-btn
                  id="sync-encounters"
                  color="orange"
                  @click="requestBackgroundSync()"
                >
                  <v-icon left>{{icons.mdiCloudUpload}}</v-icon>
                  <mrx-translation token="app.device.list.upload_records.title"></mrx-translation>
                </v-btn>
              </v-list-item-content>
            </v-list-item>

            <v-list-item class="device-row">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="badgeColor(geoPermissionStatus === 'granted')" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{icons.mdiMapMarker}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`questions.geo_permission.title`"></mrx-translation></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="device-row__value">
                <mrx-translation :token="geoPermissionText"></mrx-translation>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="device-row">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="badgeColor(geoAcquired)" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{ icons.mdiCrosshairsGps }}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.location_check.title`"></mrx-translation></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="device-row__value">
                <mrx-translation :token="geoLocationCheckText"></mrx-translation>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="device-row">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="badgeColor(versionCheck)" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{icons.mdiUpdate}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.version_check.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :token="`app.device.list.version.hrid.title`"></mrx-translation> {{hrId}}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="device-row__value">
                <mrx-translation :prepend="releaseVersion" :token="versionCheckText"></mrx-translation>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="device-row">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="badgeColor(offlineCheck)" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{icons.mdiLightningBoltCircle}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.offline.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle id="refresh-cache-hint" v-if="getMode('show') && cacheRefreshHint"><mrx-translation :token="cacheRefreshHint"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="getMode('show')">
                <v-btn
                  :disabled="!online || refreshCacheBlocked"
                  id="refresh-cache"
                  @click="cacheDialog=true"
                  outlined
                  small
                  class="device-outlined-btn"
                >
                  <mrx-translation :token="cacheRefreshText"></mrx-translation>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <v-list-item class="device-row device-row--statistics">
              <v-list-item-action class="device-row__icon">
                <v-badge :color="queueCheckIcon" overlap dot>
                  <v-avatar size="36" class="device-avatar"><v-icon size="20">{{icons.mdiChartBar}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.queue.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :append="$d(new Date(queueStartedAt))" :token="`app.device.list.queue.data_since.title`"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="totalCompletedTracks.toString()" :token="`app.device.list.queue.total_tracks.title`"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="records.toString()" :token="`app.device.list.records_in_queue.title`"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation token="app.device.list.queue.elapsed.title"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="timeSinceLastUpload.count.toString()" :token="`partials.time.${timeSinceLastUpload.label}`"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="records === 0">
                <v-btn
                  id="open-reset-statistics-dialog"
                  @click="resetDialog=true"
                  outlined
                  small
                  class="device-outlined-btn"
                >
                  <mrx-translation token="app.device.list.queue.reset.button.title"></mrx-translation>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-card-actions >
            <v-container>
              <v-row no-gutters class="justify-center align-center">
                <v-btn
                  id="device-return-to-previous"
                  class="device-return-btn"
                  depressed
                  @click="previous()"
                >
                  <mrx-translation token="app.form.return_to_assessment"></mrx-translation>
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </div>

        <!-- LOGS Tab Content -->
        <div v-show="activeTab === 'logs'" class="logs-tab-wrapper">
          <diagnostic-log-viewer class="logs-viewer-flex"></diagnostic-log-viewer>

          <v-card-actions class="mt-auto">
            <v-container>
              <v-row no-gutters class="justify-center align-center pt-2">
                <v-btn
                  id="logs-return-to-previous"
                  class="device-return-btn"
                  depressed
                  @click="previous()"
                >
                  <mrx-translation token="app.form.return_to_assessment"></mrx-translation>
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </div>

        <v-dialog
          v-if="records === 0"
          overlay-color=var(--primary-blue)
          overlay-opacity="0.9"
          v-model="resetDialog"
          persistent
          max-width="290"
        >        
          <v-card class="intro-dark">
            <v-card-title>
              <mrx-translation token="app.device.list.queue.reset.dialog.title"></mrx-translation>
            </v-card-title>
            <v-card-text class="modal-text">
              <mrx-translation token="app.device.list.queue.reset.dialog.message"></mrx-translation>
            </v-card-text>
            <v-card-actions class="px-5 pb-5 pt-0 justify-stretch align-stretch">
              <v-spacer></v-spacer>
              <v-row no-gutters class="flex xs6 pe-2">
                <v-btn 
                  id="cancel-reset-statistics" 
                  class="cancel-btn" 
                  outlined 
                  block 
                  depressed 
                  @click="resetDialog = false">
                  <mrx-translation token="app.device.list.queue.reset.dialog.cancel"></mrx-translation>
                </v-btn>
              </v-row>
              <v-row no-gutters class="flex xs6 ps-2">
                <v-btn 
                  id="confirm-reset-statistics" 
                  class="modal-btn" 
                  block 
                  depressed 
                  @click="confirmReset()">
                  <mrx-translation token="app.device.list.queue.reset.dialog.ok"></mrx-translation>
                </v-btn>
              </v-row>
              
              
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog
          overlay-color=var(--primary-blue)
          overlay-opacity="0.9"
          v-model="cacheDialog"
          persistent
          max-width="290"
        >        
          <v-card class="intro-dark">
            <v-card-title>
              <mrx-translation token="app.device.list.cache_refresh.dialog.title"></mrx-translation>
            </v-card-title>
            <v-card-text class="modal-text">
              <mrx-translation token="app.device.list.cache_refresh.dialog.message"></mrx-translation>
            </v-card-text>
            <v-card-actions class="px-5 pb-5 pt-0 justify-stretch align-stretch">
              <v-spacer></v-spacer>
              <v-row no-gutters class="flex xs6 pe-2">
                <v-btn 
                  id="cancel-refresh-cache" 
                  class="cancel-btn" 
                  outlined 
                  block 
                  depressed 
                  @click="cacheDialog=false">
                  <mrx-translation token="app.device.list.cache_refresh.dialog.cancel"></mrx-translation>
                </v-btn>
              </v-row>
              <v-row no-gutters class="flex xs6 ps-2">
                <v-btn 
                  id="confirm-refresh-cache" 
                  class="modal-btn" 
                  block 
                  depressed 
                  @click="confirmCacheRefresh()">
                  <mrx-translation token="app.device.list.cache_refresh.dialog.ok"></mrx-translation>
                </v-btn>
              </v-row>
              
              
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </v-layout>
</template>

<script>
  import mrx from '@/mixins/mrx'
  import icons from '@/mixins/icons'
  import DiagnosticLogViewer from '@/components/layouts/DiagnosticLogViewer'
  import { mapState } from 'pinia'
  import { useTestModeStore } from '@/stores/testMode'
  import { usePartnerConfigStore } from '@/stores/partnerConfig'
  import { useDeviceStatus } from '@/composables/useDeviceStatus'
  import { useNavigation } from '@/composables/useNavigation'
  import './styles/device.css'
  import './styles/settings.css'

  export default {
    name: 'Device',
    components: { DiagnosticLogViewer },
    mixins: [mrx, icons],
    setup() {
      const { previous } = useNavigation()
      return { ...useDeviceStatus(), previous }
    },
    data() {
      return {
        activeTab: 'status',
        cacheDialog: false,
        resetDialog: false,
      }
    },
    computed: {
      queueCheckIcon() {
        return 'success'
      },
      ...mapState(useTestModeStore, { getMode: 'get' }),
      ...mapState(usePartnerConfigStore, { releaseVersion: (store) => store.release.version }),
    },
    methods: {
      confirmReset() {
        this.resetDialog = false
        this.resetStatistics()
      },
      confirmCacheRefresh() {
        this.cacheDialog = false
        this.refreshCache()
      },
    },
    watch: {
      online(available) {
        if (!available) this.startPolling()
      },
      connection(available) {
        if (!available) this.startPolling()
      },
    },
  }
</script>
